import { mergeClasses } from '@fluentui/react-components';
import { useState, useMemo } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  Field,
  Input,
  Card,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Info16Regular, Add20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { Take5Submission, RiskItem, AppConfig } from '../types';
import { PhaseStrip } from './PhaseStrip';
import { TapRow } from './TapRow';
import { calculateRisk } from '../services/risk';
import { riskColors } from '../services/theme';

interface Props {
  config: AppConfig;
  initial: Partial<Take5Submission['think']>;
  onNext: (data: Take5Submission['think']) => void;
  onShowInfo: (key: string) => void;
}

interface EditableRisk {
  id: string;
  hazard: string;
  likelihood: number;
  consequence: number;
  control: string;
}

let counter = 0;
const newId = () => `r-${++counter}`;

const useStyles = makeStyles({
  body: { flex: 1, overflowY: 'auto', paddingTop: '16px', paddingBottom: '20px', paddingLeft: '18px', paddingRight: '18px' },
  section: {
    marginBottom: '14px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  stepNum: {
    backgroundColor: '#E65100',
    color: '#FFFFFF',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  infoBtn: { marginLeft: 'auto', minWidth: '44px', minHeight: '44px' },
  hazardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  },
  customOtherWrap: {
    marginTop: '10px',
    backgroundColor: '#FFF8ED',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#FFD9B0',
    borderRightColor: '#FFD9B0',
    borderBottomColor: '#FFD9B0',
    borderLeftColor: '#FFD9B0',
    borderRadius: '6px',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  riskItem: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '8px',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '12px',
    paddingRight: '12px',
    marginTop: '10px',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    position: 'relative',
    overflow: 'hidden',
  },
  removeX: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    minWidth: '36px',
    minHeight: '36px',
  },
  removeRiskBtn: {
    width: '100%',
    marginTop: '10px',
    minHeight: '40px',
  },
  hazardInput: {
    width: '100%',
    marginBottom: '4px',
    paddingRight: '40px', // leave room for the remove (X) button
    boxSizing: 'border-box',
  },
  /*
   * Risk row: L and C dropdowns in a 2-column grid, score on its own line.
   * Why on its own line: Fluent's Dropdown button has internal minWidth that
   * escapes CSS Grid minmax(0, 1fr), so attempting L|C|Score in one row
   * pushed the score off-screen on iPhone. Stacking is foolproof.
   */
  lcRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '8px',
    marginTop: '10px',
  },
  fieldCell: {
    minWidth: '0',
    '& button': {
      width: '100%',
      minWidth: '0',
      maxWidth: '100%',
    },
  },
  riskDropdown: {
    width: '100%',
    minWidth: '0',
    maxWidth: '100%',
  },
  scoreLine: {
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  scorePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    paddingTop: '5px',
    paddingBottom: '5px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: '14px',
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'none',
    letterSpacing: '0',
  },
  scorePillNumber: {
    fontSize: '14px',
    fontWeight: 800,
  },
  /* Inline warning panels — same shape as the banners on the Review screen
   * (small icon, 13px text, 3px left rule). Replaces Fluent MessageBar which
   * was rendering at desktop sizes and looked oversized on iPhone. */
  warnInline: {
    marginTop: '10px',
    borderRadius: '6px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingRight: '12px',
    fontSize: '13px',
    lineHeight: 1.45,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
  },
  warnHigh: {
    backgroundColor: '#FFF8E6',
    borderLeftColor: '#E65100',
    color: '#7A3C00',
  },
  warnExtreme: {
    backgroundColor: '#FCE8E9',
    borderLeftColor: '#E3272D',
    color: '#6F0F12',
  },
  warnIcon: {
    flexShrink: 0,
    fontSize: '16px',
    lineHeight: 1.2,
    marginTop: '1px',
  },
  warnBody: {
    flex: 1,
    minWidth: 0,
  },
  addBtn: { width: '100%', marginTop: '10px', minHeight: '48px' },
  cta: { width: '100%', marginTop: '6px', minHeight: '52px', fontSize: '15px', fontWeight: 600 },
});

export function ThinkScreen({ config, initial, onNext, onShowInfo }: Props) {
  const styles = useStyles();
  const hazards = config.hazards;
  const customHazard = useMemo(() => hazards.find(h => h.custom), [hazards]);

  const [hazardIds, setHazardIds] = useState<Set<string>>(() => {
    const set = new Set<string>();
    if (initial.hazardCategories?.length) {
      for (const label of initial.hazardCategories) {
        const cat = hazards.find(c => c.label === label);
        if (cat && !cat.custom) set.add(cat.id);
      }
    }
    return set;
  });

  const [otherText, setOtherText] = useState<string>(() => {
    if (!initial.hazardCategories?.length) return '';
    const knownLabels = new Set(hazards.filter(h => !h.custom).map(c => c.label));
    const unknown = initial.hazardCategories.filter(l => !knownLabels.has(l));
    return unknown[0] || '';
  });
  const [otherEnabled, setOtherEnabled] = useState<boolean>(() => !!otherText);

  const [risks, setRisks] = useState<EditableRisk[]>(() => {
    if (initial.risks && initial.risks.length) {
      return initial.risks.map(r => ({
        id: newId(), hazard: r.hazard, likelihood: r.likelihood,
        consequence: r.consequence, control: r.control,
      }));
    }
    return [];
  });

  const toggleHazard = (id: string) => {
    if (customHazard && id === customHazard.id) {
      setOtherEnabled(v => !v);
      return;
    }
    setHazardIds(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const updateRisk = (id: string, patch: Partial<EditableRisk>) => {
    setValidationError(null);
    setRisks(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
  };
  const addRisk = () => {
    setRisks(rs => [...rs, { id: newId(), hazard: '', likelihood: 3, consequence: 3, control: '' }]);
  };
  const removeRisk = (id: string) => {
    setRisks(rs => rs.filter(r => r.id !== id));
  };

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleNext = () => {
    if (risks.some(risk => !risk.hazard.trim())) { setValidationError('Describe each added hazard, or remove the empty assessment before continuing.'); return; }
    setValidationError(null);
    const hazardLabels: string[] = hazards
      .filter(h => !h.custom && hazardIds.has(h.id))
      .map(h => h.label);
    if (otherEnabled && otherText.trim()) {
      hazardLabels.push(otherText.trim());
    }
    const finalRisks: RiskItem[] = risks
      .filter(r => r.hazard.trim())
      .map(r => {
        const { score, level } = calculateRisk(r.likelihood, r.consequence);
        return {
          hazard: r.hazard.trim(),
          likelihood: r.likelihood,
          consequence: r.consequence,
          riskScore: score,
          riskLevel: level,
          control: r.control.trim(),
        };
      });
    onNext({ hazardCategories: hazardLabels, risks: finalRisks });
  };

  return (
    <>
      <PhaseStrip step={2} />
      <div className={styles.body} data-screen-body>
        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>3</span>
            Hazard categories
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('hazardCat')}
              className={styles.infoBtn}
              aria-label="More info"
            />
          </div>
          <div style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, marginBottom: 10 }}>
            Tap every category that could affect your work today.
          </div>
          <div className={styles.hazardGrid}>
            {hazards.filter(h => !h.custom).map(h => (
              <TapRow
                key={h.id}
                variant="chip"
                checked={hazardIds.has(h.id)}
                onToggle={() => toggleHazard(h.id)}
              >
                {h.label}
              </TapRow>
            ))}
            {customHazard && (
              <TapRow
                variant="chip"
                checked={otherEnabled}
                onToggle={() => toggleHazard(customHazard.id)}
              >
                {customHazard.label}
              </TapRow>
            )}
          </div>
          {otherEnabled && (
            <div className={styles.customOtherWrap}>
              <Field label="Describe the other hazard">
                <Input
                  value={otherText}
                  size="large"
                  onChange={(_, data) => setOtherText(data.value)}
                  placeholder="e.g. Asbestos in old pipework"
                  input={{ style: { fontSize: '16px' }, autoComplete: 'off', maxLength: 500 }}
                />
              </Field>
            </div>
          )}
        </Card>

        <Card className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.stepNum}>4</span>
            Risk assessment (L × C)
            <Button
              appearance="subtle"
              icon={<Info16Regular />}
              onClick={() => onShowInfo('risk')}
              className={styles.infoBtn}
              aria-label="View risk matrix"
            />
          </div>
          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: 10, lineHeight: 1.5 }}>
            For each hazard: rate <strong>Likelihood</strong> (1→5) × <strong>Consequence</strong> (1→5)<br/>
            <span style={{ color: riskColors.Low }}>1–2 Low</span>{' · '}
            <span style={{ color: riskColors.Medium }}>3–6 Medium</span>{' · '}
            <span style={{ color: riskColors.High }}>7–12 High</span>{' · '}
            <span style={{ color: riskColors.Extreme }}>13–25 Extreme</span>
          </div>
          {risks.map(r => (
            <RiskRow
              key={r.id}
              risk={r}
              styles={styles}
              onChange={updateRisk}
              onRemove={removeRisk}
              onShowInfo={onShowInfo}
            />
          ))}
          <Button
            appearance="outline"
            icon={<Add20Regular />}
            onClick={addRisk}
            disabled={risks.length >= 20}
            size="large"
            className={styles.addBtn}
          >
            Add risk assessment
          </Button>
        </Card>

        {validationError && <MessageBar intent="error" role="alert"><MessageBarBody>{validationError}</MessageBarBody></MessageBar>}
        <Button
          appearance="primary"
          size="large"
          onClick={handleNext}
          className={styles.cta}
        >
          Next · Apply Controls
        </Button>
      </div>
    </>
  );
}

interface RiskRowProps {
  risk: EditableRisk;
  styles: ReturnType<typeof useStyles>;
  onChange: (id: string, patch: Partial<EditableRisk>) => void;
  onRemove: (id: string) => void;
  onShowInfo: (key: string) => void;
}

function RiskRow({ risk, styles, onChange, onRemove, onShowInfo }: RiskRowProps) {
  const { score, level } = calculateRisk(risk.likelihood, risk.consequence);
  const levelColor = riskColors[level];

  return (
    <div className={styles.riskItem} style={{ borderLeftColor: levelColor }}>
      <Button
        appearance="subtle"
        icon={<Dismiss20Regular />}
        className={styles.removeX}
        onClick={() => onRemove(risk.id)}
        aria-label="Remove hazard"
      />
      <Input
        value={risk.hazard}
        size="large"
        onChange={(_, data) => onChange(risk.id, { hazard: data.value })}
        aria-label="Hazard description"
        placeholder="Describe the hazard..."
        className={styles.hazardInput}
        input={{ style: { fontSize: '16px' }, autoComplete: 'off', maxLength: 500 }}
      />
      <div className={styles.lcRow}>
        <Field
          className={styles.fieldCell}
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: '12px' }}>
              Likelihood
              <Button
                appearance="subtle"
                size="small"
                icon={<Info16Regular />}
                onClick={() => onShowInfo('likelihood')}
                aria-label="Likelihood help"
                style={{ minWidth: 24, minHeight: 24, padding: 0 }}
              />
            </span>
          }
        >
          <Dropdown
            className={styles.riskDropdown}
            value={String(risk.likelihood)}
            selectedOptions={[String(risk.likelihood)]}
            onOptionSelect={(_, data) => {
              if (data.optionValue) onChange(risk.id, { likelihood: parseInt(data.optionValue) });
            }}
            size="medium"
            button={{ style: { fontSize: '15px', minWidth: 0, width: '100%' } }}
          >
            {[1,2,3,4,5].map(n => <Option key={n} value={String(n)}>{String(n)}</Option>)}
          </Dropdown>
        </Field>
        <Field
          className={styles.fieldCell}
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: '12px' }}>
              Consequence
              <Button
                appearance="subtle"
                size="small"
                icon={<Info16Regular />}
                onClick={() => onShowInfo('consequence')}
                aria-label="Consequence help"
                style={{ minWidth: 24, minHeight: 24, padding: 0 }}
              />
            </span>
          }
        >
          <Dropdown
            className={styles.riskDropdown}
            value={String(risk.consequence)}
            selectedOptions={[String(risk.consequence)]}
            onOptionSelect={(_, data) => {
              if (data.optionValue) onChange(risk.id, { consequence: parseInt(data.optionValue) });
            }}
            size="medium"
            button={{ style: { fontSize: '15px', minWidth: 0, width: '100%' } }}
          >
            {[1,2,3,4,5].map(n => <Option key={n} value={String(n)}>{String(n)}</Option>)}
          </Dropdown>
        </Field>
      </div>
      <div className={styles.scoreLine}>
        <span>Risk score</span>
        <span
          className={styles.scorePill}
          style={{ background: `${levelColor}1f`, color: levelColor }}
        >
          <span className={styles.scorePillNumber}>{score}</span>
          <span>·</span>
          <span>{level}</span>
        </span>
      </div>
      <Input
        value={risk.control}
        size="large"
        onChange={(_, data) => onChange(risk.id, { control: data.value })}
        aria-label="Control measure"
        placeholder="Control measure for this hazard..."
        style={{ width: '100%', marginTop: 10 }}
        input={{ style: { fontSize: '16px' }, autoComplete: 'off', maxLength: 1000, 'aria-label': 'Control measure' }}
      />
      <Button
        appearance="subtle"
        icon={<Dismiss20Regular />}
        className={styles.removeRiskBtn}
        onClick={() => onRemove(risk.id)}
      >
        Remove risk assessment
      </Button>
      {level === 'High' && (
        <div className={mergeClasses(styles.warnInline, styles.warnHigh)}>
          <span className={styles.warnIcon} aria-hidden="true">⚠</span>
          <div className={styles.warnBody}>
            <strong>High risk.</strong> Additional engineering or isolation controls required. Consider a SWMS and confirm with your supervisor.
          </div>
        </div>
      )}
      {level === 'Extreme' && (
        <div className={mergeClasses(styles.warnInline, styles.warnExtreme)}>
          <span className={styles.warnIcon} aria-hidden="true">🛑</span>
          <div className={styles.warnBody}>
            <strong>Extreme risk · DO NOT PROCEED.</strong> Stop work immediately. SWMS is mandatory. Call your supervisor.
          </div>
        </div>
      )}
    </div>
  );
}
